/**
 * App shell.
 *
 * macOS-aesthetic floating window with a persistent 20:30:50
 * workspace inside: the document tree on the left, the chat
 * assistant in the middle, and the variation analysis on the
 * right. Settings is reachable from anywhere via a Settings
 * button in the chrome that opens a sheet.
 *
 * Per the MakeHQ design, the product is "MakeHQ" in the chrome and
 * the underlying platform is "Contractor Variation Platform" in the
 * code, repo, and documentation.
 */

import { useEffect, useRef, useState } from "react";
import { NavLink, Route, Routes, useNavigate } from "react-router-dom";
import { Icon } from "./icons";
import { ToastProvider } from "./Toasts";
import { useAuth } from "./auth/AuthContext";
import { Workspace } from "./views/Workspace";
import { ContractorChatPage } from "./views/ContractorChatPage";
import { ContactsPage } from "./views/ContactsPage";
import { SettingsSheet } from "./views/Settings";
import type { AssessmentRecord, DeterminationPayload } from "./api/client";

export function App() {
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [activeAssessment, setActiveAssessment] = useState<AssessmentRecord | null>(null);
  const [, setActiveDetermination] = useState<DeterminationPayload | null>(null);

  return (
    <ToastProvider>
      <div className="app" role="application" aria-label="Variation Management Platform">
        <Topbar
          assessment={activeAssessment}
          onOpenSettings={() => setSettingsOpen(true)}
        />
        <TabStrip />
        <Routes>
          <Route
            index
            element={
              <Workspace
                onActiveAssessmentChange={setActiveAssessment}
                onActiveDeterminationChange={setActiveDetermination}
              />
            }
          />
          <Route path="chat" element={<ContractorChatPage />} />
          <Route path="contacts" element={<ContactsPage />} />
        </Routes>
        <SettingsSheet open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      </div>
    </ToastProvider>
  );
}

// Tab strip below the toolbar. Routes are URL-driven so each tab
// is a shareable link and the browser back/forward buttons behave
// the way operators expect. The strip lives outside the routed
// children so it stays mounted across navigations and the active
// indicator transitions feel native.
function TabStrip() {
  // Each tab carries a small icon next to the label so the strip
  // reads as a navigation bar at a glance, not a row of links.
  // The icons reuse the existing Icon set; nothing else in the
  // app needs to change.
  const tabs: Array<{
    to: string;
    label: string;
    end: boolean;
    icon: keyof typeof Icon;
  }> = [
    { to: "/", label: "Workspace", end: true, icon: "doc" },
    { to: "/chat", label: "Chat", end: false, icon: "sparkle" },
    { to: "/contacts", label: "Contacts", end: false, icon: "ledger" },
  ];
  return (
    <nav className="tab-strip" aria-label="Primary">
      {tabs.map((tab) => {
        const IconCmp = Icon[tab.icon];
        return (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) =>
              isActive ? "tab tab-active" : "tab"
            }
          >
            <span className="tab-icon" aria-hidden>
              <IconCmp />
            </span>
            <span className="tab-label">{tab.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}

function Topbar({
  assessment,
  onOpenSettings,
}: {
  assessment: AssessmentRecord | null;
  onOpenSettings: () => void;
}) {
  // Breadcrumbs reflect the active assessment when there is one;
  // otherwise the bar is intentionally minimal. The descriptive
  // doc-title block was removed (operators reported it as noise
  // above the tab strip); Settings + UserMenu are the only
  // affordances kept on the toolbar so the chrome stays useful
  // without competing with the tabs.
  const crumbs: string[] = [];
  if (assessment) {
    if (assessment.contract_id) crumbs.push(assessment.contract_id);
    crumbs.push(
      assessment.workflow_template === "site_instruction"
        ? "Site instruction"
        : "Variation",
    );
    crumbs.push(assessment.variation_id);
  }

  return (
    <>
      <div className="titlebar">
        <div className="title-center">
          <span className="title-app-name">Variation Management Platform</span>
          {crumbs.length > 0 && <span className="title-sep">—</span>}
          {crumbs.length > 0 && (
            <nav className="breadcrumb" aria-label="Breadcrumb">
              {crumbs.map((crumb, i) => (
                <span key={`${i}-${crumb}`} className="crumb">
                  {i > 0 && <span className="crumb-sep">›</span>}
                  <span className={i === crumbs.length - 1 ? "crumb-here" : ""}>
                    {crumb}
                  </span>
                </span>
              ))}
            </nav>
          )}
          {assessment?.issued_at && (
            <span className="title-issued-pill" title={
              `Issued by ${assessment.issued_by ?? "unknown"} on ` +
              new Date(assessment.issued_at).toLocaleString("en-AU", {
                dateStyle: "medium",
                timeStyle: "short",
              })
            }>
              <span className="title-issued-dot" />
              ISSUED
            </span>
          )}
        </div>
        <div className="title-actions">
          <button
            className="btn btn-sm btn-settings"
            type="button"
            onClick={onOpenSettings}
            title="Workspace settings (admin)"
          >
            <Icon.cog />
            Settings
          </button>
          <UserMenu />
        </div>
      </div>
    </>
  );
}

function _initialsFromPrincipal(emailOrName: string | null | undefined): string {
  if (!emailOrName) {
    return "?";
  }
  // For an email like "alex.harper@example.com" we take "AH"; for
  // a free-form display name like "Alex Harper" we also take "AH".
  // Single-token inputs collapse to the first two characters.
  const local = emailOrName.split("@", 1)[0];
  const tokens = local.split(/[\s._\-]+/).filter(Boolean);
  if (tokens.length === 0) {
    return "?";
  }
  if (tokens.length === 1) {
    return tokens[0].slice(0, 2).toUpperCase();
  }
  return (tokens[0][0] + tokens[1][0]).toUpperCase();
}

function UserMenu() {
  const { principal, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  // Close the popover on outside click. The handler runs on
  // mousedown so the click that opened the menu does not also
  // close it via the document listener.
  useEffect(() => {
    if (!open) {
      return;
    }
    function onDocumentMouseDown(event: MouseEvent) {
      if (
        popoverRef.current !== null
        && !popoverRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocumentMouseDown);
    return () => document.removeEventListener("mousedown", onDocumentMouseDown);
  }, [open]);

  if (principal === null) {
    return null;
  }

  // Prefer display_name → email → service-account label. The
  // initials function strips the email-local-part so an address
  // like "alex.harper@example.com" yields "AH".
  const display =
    principal.display_name
    ?? principal.email
    ?? (principal.service_account_id !== null ? "Service account" : "Account");
  const initials = _initialsFromPrincipal(display);

  async function onLogout() {
    setOpen(false);
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    <div className="user-menu" ref={popoverRef}>
      <button
        type="button"
        className="avatar"
        aria-label={`Account menu, signed in as ${display}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {initials}
      </button>
      {open && (
        <div className="user-menu-pop" role="menu">
          <div className="user-menu-id">
            <div className="user-menu-name">{display}</div>
            <div className="user-menu-role">{principal.role} · {principal.tenant_id}</div>
          </div>
          {!principal.mfa_enrolled && (
            <button
              type="button"
              className="user-menu-item"
              onClick={() => {
                setOpen(false);
                navigate("/mfa/enrol");
              }}
              role="menuitem"
            >
              Set up two-factor auth
            </button>
          )}
          {principal.role === "platform_owner" && (
            <button
              type="button"
              className="user-menu-item"
              onClick={() => {
                setOpen(false);
                navigate("/admin/platform");
              }}
              role="menuitem"
            >
              Platform admin
            </button>
          )}
          <button
            type="button"
            className="user-menu-item user-menu-danger"
            onClick={onLogout}
            role="menuitem"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
